import { formatAddress, formatDate, formatName, sanitizePortalHTMLContent } from "@tellescope/utils";
import { formatStoredTime } from "@tellescope/forms";
import { RichTextDisplay } from "@tellescope/ui";
import type { ReactNode } from "react";
import type { Answer } from "./types";
import { HIDDEN_ANSWER_TYPES } from "./constants";

/**
 * Renders a form answer value to a displayable React node.
 * Handles 30+ answer types with appropriate formatting.
 */
export const renderAnswer = (answer: Answer, answerIsHTML?: boolean): ReactNode => {
  if (!answer.value) return null;
  if (HIDDEN_ANSWER_TYPES.includes(answer.type)) return null;

  if ((answerIsHTML || answer.type === "Rich Text") && typeof answer.value === "string") {
    return (
      <div
        className="prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: sanitizePortalHTMLContent(answer.value, "enduser") }}
      />
    );
  }

  switch (answer.type) {
    case "string":
    case "description":
    case "stringLong":
    case "email":
    case "phone":
    case "Timezone":
    case "Emotii":
    case "Stripe":
    case "Dropdown":
    case "Appointment Booking":
      return answer.value;

    case "number":
      return answer.value.toLocaleString();

    case "files":
    case "file": {
      const files = Array.isArray(answer.value) ? answer.value : [answer.value];
      return files.map((file) => `${file.name} ${file.type ? `(${file.type})` : ""}`.trim()).join(", ");
    }

    case "Rich Text":
      return <RichTextDisplay content={answer.value} />;

    case "date":
    case "dateString":
      return formatDate(answer.value);

    case "rating":
      return `${answer.value}/10`;

    case "Time":
      return formatStoredTime(answer.value);

    case "Allergies":
      return answer.value.map((allergy) => allergy.display).join(", ");

    case "Chargebee":
      return answer.value.url;

    case "Address":
      return formatAddress(answer.value);

    case "Table Input":
      return (
        <div className="space-y-1">
          {answer.value.map((row, rowIdx) => (
            <div key={rowIdx} className="text-sm">
              {row.map((cell) => `${cell.label}: ${cell.entry}`).join(", ")}
            </div>
          ))}
        </div>
      );

    case "ranking":
      return (
        <ol className="list-decimal list-inside space-y-0.5">
          {answer.value.map((value, i) => (
            <li key={i} className="text-sm">
              {value}
            </li>
          ))}
        </ol>
      );

    case "signature":
      return `${answer.value.signed ? "Signed" : "Not Signed"}: ${answer.value.fullName}`;

    case "Medications":
      return answer.value.map((medication) => medication.displayTerm).join(", ");

    case "Database Select":
      return answer.value.map((item) => item.text).join(", ");

    case "multiple_choice":
      return answer.value.join(", ");

    case "Related Contacts":
      return answer.value.map((contact) => formatName(contact)).join(", ");

    case "Insurance": {
      const elements: string[] = [];
      if (answer.value.memberId) {
        elements.push(`Member ID: ${answer.value.memberId}`);
      }
      if (answer.value.groupNumber) {
        elements.push(`Group Number: ${answer.value.groupNumber}`);
      }
      if (answer.value.eligibility) {
        elements.push(`Eligibility: ${answer.value.eligibility}`);
      }
      return elements.join(", ");
    }

    case "Height":
      return `${answer.value.feet}' ${answer.value.inches}"`;

    default:
      return null;
  }
};
